import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Header.css';

const NAV_SECTIONS = [
  { id: 'overview', label: 'Account Overview', icon: '📋' },
  { id: 'settle', label: 'Settle Account', icon: '💳' },
  { id: 'arrange', label: 'Payment Plan', icon: '📅' },
  { id: 'negotiate', label: 'Negotiate', icon: '🤝' },
  { id: 'objection', label: 'Raise Objection', icon: '⚠️' },
  { id: 'counselling', label: 'Debt Counselling', icon: '🛟' },
  { id: 'contact', label: 'Contact Us', icon: '📞' },
];

export default function Header({ activeSection, onNavigate }) {
  const [isOpen, setIsOpen] = useState(false);
  const navRef = useRef(null);
  const hamburgerRef = useRef(null);

  const toggleMenu = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Close menu on navigation
  const handleNavClick = useCallback((sectionId) => {
    if (onNavigate) {
      onNavigate(sectionId);
    }
    closeMenu();
  }, [onNavigate, closeMenu]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => document.body.classList.remove('no-scroll');
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeMenu();
        hamburgerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeMenu]);

  // Close menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isOpen) {
        closeMenu();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, closeMenu]);

  // Trap focus inside nav when open
  useEffect(() => {
    if (!isOpen || !navRef.current) return;

    const focusableEls = navRef.current.querySelectorAll(
      'a[href], button, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableEls.length > 0) {
      focusableEls[0].focus();
    }
  }, [isOpen]);

  return (
    <header className="header" role="banner">
      <div className="header__inner">
        {/* Logo */}
        <span className="header__logo" aria-label="Dola Money">
          <img src="/dola-icon.png" alt="Dola Money" className="header__logo-img" />
        </span>

        {/* Hamburger Button - Mobile/Tablet */}
        <button
          ref={hamburgerRef}
          className={`header__hamburger ${isOpen ? 'header__hamburger--open' : ''}`}
          onClick={toggleMenu}
          aria-expanded={isOpen}
          aria-controls="main-nav"
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
        >
          <span className="header__hamburger-line" aria-hidden="true"></span>
          <span className="header__hamburger-line" aria-hidden="true"></span>
          <span className="header__hamburger-line" aria-hidden="true"></span>
        </button>

        {/* Overlay (mobile) */}
        <div
          className={`header__nav-overlay ${isOpen ? 'header__nav-overlay--visible' : ''}`}
          onClick={closeMenu}
          aria-hidden="true"
        />

        {/* Navigation */}
        <nav
          id="main-nav"
          ref={navRef}
          className={`header__nav ${isOpen ? 'header__nav--open' : ''}`}
          role="navigation"
          aria-label="Main navigation"
        >
          <ul className="header__nav-list">
            {NAV_SECTIONS.map(({ id, label, icon }) => (
              <li key={id} className="header__nav-item">
                <a
                  href={`#${id}`}
                  className={`header__nav-link ${
                    activeSection === id ? 'header__nav-link--active' : ''
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(id);
                  }}
                  aria-current={activeSection === id ? 'true' : undefined}
                >
                  <span className="header__nav-icon" aria-hidden="true">{icon}</span>
                  <span className="header__nav-label">{label}</span>
                </a>
              </li>
            ))}
          </ul>

          {/* Help CTA in mobile nav */}
          <div className="header__nav-help">
            <button
              className="header__nav-help-btn"
              onClick={() => handleNavClick('contact')}
            >
              📞 Need Help? Contact Us
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
}
